import { useState } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Bed, Plus, MoreVertical, ArrowRight, GripHorizontal, X } from 'lucide-react';
import { useWorkoutStore } from '../stores/workoutStore';
import type { DayData, Session } from '../types/workout';
import { isRestDay, isWorkoutDay, getDaySessions, getDayLabel, walkSeasonWeeks } from '../types/workout';
import { ExerciseList } from './ExerciseList';
import { AddExerciseDialog } from './AddExerciseDialog';
import { DayFeedback } from './DayFeedback';
import { cn, formatWorkoutDate } from '../lib/utils';

interface DayCardProps {
  date: string;
  dayData: DayData;
}

interface AddDialogState {
  open: boolean;
  sessionIndex: number;
}

// Fallback modality options shown in the category dropdown when the user's
// own `selected_activities` don't cover what the admin wants. These match
// the canonical labels the agent emits and the Playground form uses.
const DEFAULT_SESSION_CATEGORIES = [
  'Strength Training',
  'Weightlifting',
  'Run',
  'Cycling',
  'Swim',
  'Yoga',
  'Pilates',
  'HIIT',
  'Rowing',
  'Walk',
  'Hike',
  'Mobility',
  'Other',
];

/** Union of (current value, user's selected activities, defaults) preserving order:
 *  current value first, then user-selected, then remaining defaults. Stable for
 *  rendering as an option list. */
function buildCategoryOptions(
  currentValue: string | undefined,
  userActivities: string[],
): string[] {
  const out: string[] = [];
  const push = (v: string | undefined) => {
    if (!v) return;
    const trimmed = v.trim();
    if (!trimmed) return;
    if (!out.includes(trimmed)) out.push(trimmed);
  };
  push(currentValue);
  userActivities.forEach(push);
  DEFAULT_SESSION_CATEGORIES.forEach(push);
  return out;
}

// Sortable wrapper for a single session card. Uses dnd-kit/sortable so
// reordering is keyboard-accessible and restricted to the parent SortableContext.
function SortableSession({
  date,
  session,
  sessionIndex,
  dayGoal,
  userActivities,
  onAddExercise,
  onCategoryChange,
  onNameChange,
  onGoalChange,
  onDelete,
}: {
  date: string;
  session: Session;
  sessionIndex: number;
  dayGoal: string;
  userActivities: string[];
  onAddExercise: (sessionIndex: number) => void;
  onCategoryChange: (sessionIndex: number, category: string) => void;
  onNameChange: (sessionIndex: number, name: string) => void;
  onGoalChange: (sessionIndex: number, goal: string) => void;
  onDelete: (sessionIndex: number) => void;
}) {
  const id = `session:${date}:${sessionIndex}`;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const categoryOptions = buildCategoryOptions(session.category, userActivities);

  // Click-to-edit state for the session title (session.name) — distinct
  // from the category dropdown next to it. Free text.
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');

  // Click-to-edit state for the per-session free-text "workout structure"
  // (session.goal). Used by admins for cardio descriptions where the
  // structured {exercise, sets, reps, weight} rows don't fit.
  const sessionGoal = session.goal ?? '';
  const showSessionGoal = sessionGoal && sessionGoal !== dayGoal;
  const [isEditingGoal, setIsEditingGoal] = useState(false);
  const [goalDraft, setGoalDraft] = useState('');

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="rounded-md border border-gray-100 bg-gray-50/40 p-2.5"
    >
      {/* Header row: drag handle + modality select + custom name + delete.
          `min-w-0` on the select and `truncate` on the name keep long
          values from pushing the trash X off the column. */}
      <div className="mb-2 flex items-center gap-1.5">
        <button
          {...listeners}
          {...attributes}
          className="shrink-0 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
          title="Drag to reorder sessions"
        >
          <GripHorizontal className="size-3.5" />
        </button>
        <select
          value={session.category ?? ''}
          onChange={(e) => onCategoryChange(sessionIndex, e.target.value)}
          title="Training modality"
          className="min-w-0 max-w-[140px] truncate rounded-full border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700 focus:outline-hidden focus:ring-1 focus:ring-primary"
        >
          {session.category === undefined && <option value="">(unset)</option>}
          {categoryOptions.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
        {isEditingName ? (
          <input
            autoFocus
            type="text"
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={() => {
              setIsEditingName(false);
              const trimmed = nameDraft.trim();
              if (trimmed !== (session.name ?? '')) {
                onNameChange(sessionIndex, trimmed);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                (e.target as HTMLInputElement).blur();
              } else if (e.key === 'Escape') {
                setIsEditingName(false);
                setNameDraft(session.name ?? '');
              }
            }}
            placeholder="Add session title…"
            className="min-w-0 flex-1 truncate rounded border border-gray-300 px-1.5 py-0.5 text-xs font-medium text-gray-800 focus:outline-hidden focus:ring-1 focus:ring-primary"
          />
        ) : (
          <span
            onClick={() => {
              setNameDraft(session.name ?? '');
              setIsEditingName(true);
            }}
            title="Click to edit session title"
            className={cn(
              'min-w-0 flex-1 truncate cursor-text rounded px-1 py-0.5 text-xs font-medium hover:bg-gray-100',
              session.name && session.name !== session.category
                ? 'text-gray-800'
                : 'text-gray-400 italic',
            )}
          >
            {session.name && session.name !== session.category
              ? session.name
              : 'Add session title…'}
          </span>
        )}
        <button
          onClick={() => onDelete(sessionIndex)}
          title="Remove this session"
          className="ml-auto shrink-0 rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
        >
          <X className="size-3.5" />
        </button>
      </div>

      {/* Session free-text "workout structure" — repurposes session.goal.
          Click-to-edit textarea; rendered even when empty so admins have
          an affordance to add cardio descriptions like "5x400m at threshold". */}
      {isEditingGoal ? (
        <textarea
          autoFocus
          className="w-full mb-2 text-xs text-gray-700 bg-white border border-gray-300 rounded-md px-2 py-1 resize-none focus:outline-hidden focus:ring-1 focus:ring-primary"
          rows={2}
          value={goalDraft}
          onChange={(e) => setGoalDraft(e.target.value)}
          onBlur={() => {
            setIsEditingGoal(false);
            if (goalDraft !== sessionGoal) {
              onGoalChange(sessionIndex, goalDraft);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setIsEditingGoal(false);
              setGoalDraft(sessionGoal);
            }
          }}
        />
      ) : (
        <p
          onClick={() => {
            setGoalDraft(sessionGoal);
            setIsEditingGoal(true);
          }}
          title="Click to edit workout structure"
          className={cn(
            'mb-2 text-xs cursor-text rounded px-1 py-0.5 wrap-break-word hover:bg-gray-100',
            showSessionGoal ? 'text-gray-500' : 'text-gray-400 italic',
          )}
        >
          {showSessionGoal ? sessionGoal : 'Add workout structure…'}
        </p>
      )}

      <ExerciseList date={date} sessionIndex={sessionIndex} exercises={session.exercises} />

      <button
        className="w-full mt-2 px-2 py-1.5 text-xs border border-dashed border-gray-300 rounded-md hover:bg-white flex items-center justify-center gap-1.5 text-gray-600"
        onClick={() => onAddExercise(sessionIndex)}
      >
        <Plus className="h-3.5 w-3.5" />
        Add Exercise
      </button>
    </div>
  );
}

export function DayCard({ date, dayData }: DayCardProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [showMoveSubmenu, setShowMoveSubmenu] = useState(false);
  const [addDialog, setAddDialog] = useState<AddDialogState>({ open: false, sessionIndex: 0 });
  const [isEditingGoal, setIsEditingGoal] = useState(false);
  const [goalDraft, setGoalDraft] = useState('');
  // Day-title editor (writes day.category). Two modes:
  //   'select' — dropdown of MC V categories + user activities + "Other…"
  //   'input'  — free text, entered when admin picks "Other…"
  const [isEditingDayTitle, setIsEditingDayTitle] = useState(false);
  const [dayTitleMode, setDayTitleMode] = useState<'select' | 'input'>('select');
  const [dayTitleDraft, setDayTitleDraft] = useState('');
  const {
    season,
    currentUser,
    convertToRestDay,
    convertToWorkoutDay,
    updateWorkoutGoal,
    moveDayToDate,
    addSession,
    deleteSession,
    updateSessionCategory,
    updateDayCategory,
    updateSessionName,
    updateSessionGoal,
  } = useWorkoutStore();

  // Modalities the user picked in onboarding — drive the category dropdown
  // so admins see "this user's own activities" first.
  const userActivities = Array.isArray(currentUser?.selected_activities)
    ? (currentUser?.selected_activities as string[])
    : [];

  // Default a newly-added session to the user's first non-current modality,
  // or "Strength Training" when the user has no activities tagged.
  function pickDefaultNewCategory(existing: Session[]): string {
    const used = new Set(existing.map((s) => s.category).filter(Boolean) as string[]);
    const candidate = userActivities.find((a) => !used.has(a));
    if (candidate) return candidate;
    if (userActivities.length > 0) return userActivities[0];
    return 'Strength Training';
  }

  // Outer DnD: this whole card is draggable & droppable so users can swap days.
  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
    id: date,
  });

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: date,
  });

  const handleConvertToRestDay = () => {
    convertToRestDay(date);
    setShowMenu(false);
  };

  const handleConvertToWorkoutDay = () => {
    convertToWorkoutDay(date);
    setShowMenu(false);
  };

  const moveTargets: { date: string; label: string }[] = (() => {
    if (!season) return [];
    const out: { date: string; label: string }[] = [];
    for (const { week } of walkSeasonWeeks(season)) {
      for (const d of Object.keys(week.days).sort()) {
        if (d === date) continue;
        out.push({ date: d, label: formatWorkoutDate(d) });
      }
    }
    return out;
  })();

  const handleMove = async (target: string) => {
    setShowMenu(false);
    setShowMoveSubmenu(false);
    await moveDayToDate(date, target);
  };

  if (isRestDay(dayData)) {
    return (
      <div
        ref={(node) => {
          setDragRef(node);
          setDropRef(node);
        }}
        className={cn(
          'bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 p-4 transition-all relative',
          isDragging && 'opacity-50',
          isOver && 'border-primary bg-primary/5',
        )}
      >
        <div className="flex items-center justify-between mb-4">
          <div {...listeners} {...attributes} className="cursor-move">
            <GripVertical className="h-5 w-5 text-gray-400" />
          </div>
          <span className="font-semibold text-gray-700">{formatWorkoutDate(date)}</span>
          <button
            onClick={() => {
              setShowMenu((v) => !v);
              setShowMoveSubmenu(false);
            }}
            className="p-1 hover:bg-gray-200 rounded"
          >
            <MoreVertical className="h-4 w-4 text-gray-500" />
          </button>
        </div>

        <div className="flex flex-col items-center justify-center py-8">
          <Bed className="h-12 w-12 text-gray-400 mb-2" />
          <p className="text-gray-500 font-medium">Rest Day</p>
        </div>

        <button
          onClick={handleConvertToWorkoutDay}
          className="w-full mt-4 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Convert to Workout
        </button>

        {showMenu && (
          <div className="absolute right-2 top-12 w-48 bg-white rounded-md shadow-lg border z-10">
            <button
              onClick={handleConvertToWorkoutDay}
              className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
            >
              Convert to Workout
            </button>
            <MoveSubmenu
              show={showMoveSubmenu}
              onToggle={() => setShowMoveSubmenu((v) => !v)}
              targets={moveTargets}
              onPick={handleMove}
            />
          </div>
        )}

        {/* Admin validation: rest-day prescription matters too — sometimes
            the agent gives the wrong day off (e.g. rest the day before a
            big race, or rest after only light yoga). */}
        <DayFeedback date={date} />
      </div>
    );
  }

  if (isWorkoutDay(dayData)) {
    const sessions = getDaySessions(dayData);
    const dayLabel = getDayLabel(dayData);
    const dayGoal = dayData.goal ?? sessions[0]?.goal ?? '';

    return (
      <div
        ref={(node) => {
          setDragRef(node);
          setDropRef(node);
        }}
        className={cn(
          'bg-white rounded-lg border border-gray-200 shadow-xs p-3 transition-all relative',
          isDragging && 'opacity-50',
          isOver && 'border-primary border-2 bg-primary/5',
        )}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2 flex-1">
            <div {...listeners} {...attributes} className="cursor-move">
              <GripVertical className="h-5 w-5 text-gray-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">{formatWorkoutDate(date)}</h3>
              {/* Day title editor — click to swap to a dropdown of MC V
                  categories + user activities. Picking "Other…" swaps the
                  dropdown for a free-text input. Writes day.category. */}
              {isEditingDayTitle ? (
                dayTitleMode === 'select' ? (
                  <select
                    autoFocus
                    value={dayTitleDraft}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '__other__') {
                        setDayTitleMode('input');
                        setDayTitleDraft('');
                        return;
                      }
                      setDayTitleDraft(v);
                      setIsEditingDayTitle(false);
                      if (v !== (dayData.category ?? '')) {
                        updateDayCategory(date, v);
                      }
                    }}
                    onBlur={() => {
                      // Allow blur to close only when the user didn't switch
                      // into free-text mode. (When they pick "Other…", focus
                      // moves to the new input which fires its own blur.)
                      if (dayTitleMode === 'select') {
                        setIsEditingDayTitle(false);
                      }
                    }}
                    className="text-sm text-gray-700 bg-white border border-gray-300 rounded px-1.5 py-0.5 focus:outline-hidden focus:ring-1 focus:ring-primary"
                  >
                    {buildCategoryOptions(dayData.category, userActivities).map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                    <option value="__other__">Other…</option>
                  </select>
                ) : (
                  <input
                    autoFocus
                    type="text"
                    value={dayTitleDraft}
                    onChange={(e) => setDayTitleDraft(e.target.value)}
                    onBlur={() => {
                      setIsEditingDayTitle(false);
                      setDayTitleMode('select');
                      const trimmed = dayTitleDraft.trim();
                      if (trimmed && trimmed !== (dayData.category ?? '')) {
                        updateDayCategory(date, trimmed);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        (e.target as HTMLInputElement).blur();
                      } else if (e.key === 'Escape') {
                        setIsEditingDayTitle(false);
                        setDayTitleMode('select');
                      }
                    }}
                    placeholder="Custom title…"
                    className="text-sm text-gray-700 bg-white border border-gray-300 rounded px-1.5 py-0.5 focus:outline-hidden focus:ring-1 focus:ring-primary"
                  />
                )
              ) : (
                <p
                  onClick={() => {
                    setDayTitleDraft(dayData.category ?? dayLabel);
                    setDayTitleMode('select');
                    setIsEditingDayTitle(true);
                  }}
                  title="Click to edit day title"
                  className={cn(
                    'text-sm cursor-text rounded px-1 py-0.5 hover:bg-gray-50',
                    dayLabel ? 'text-gray-600' : 'text-gray-400 italic',
                  )}
                >
                  {dayLabel || 'Add day title…'}
                </p>
              )}
            </div>
          </div>
          <div className="relative">
            <button
              onClick={() => {
                setShowMenu((v) => !v);
                setShowMoveSubmenu(false);
              }}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <MoreVertical className="h-4 w-4 text-gray-500" />
            </button>

            {showMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg border z-20">
                <button
                  onClick={handleConvertToRestDay}
                  className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-50 text-red-600"
                >
                  Convert to Rest Day
                </button>
                <MoveSubmenu
                  show={showMoveSubmenu}
                  onToggle={() => setShowMoveSubmenu((v) => !v)}
                  targets={moveTargets}
                  onPick={handleMove}
                />
              </div>
            )}
          </div>
        </div>

        {/* Day-level goal (legacy field, still useful) */}
        {isEditingGoal ? (
          <textarea
            autoFocus
            className="w-full text-sm text-gray-700 bg-gray-50 border border-gray-300 rounded-md px-2 py-1 mb-3 resize-none focus:outline-hidden focus:ring-1 focus:ring-primary"
            rows={2}
            value={goalDraft}
            onChange={(e) => setGoalDraft(e.target.value)}
            onBlur={() => {
              setIsEditingGoal(false);
              if (goalDraft !== dayGoal) {
                updateWorkoutGoal(date, goalDraft);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setIsEditingGoal(false);
                setGoalDraft(dayGoal);
              }
            }}
          />
        ) : (
          <p
            className={cn(
              'text-sm mb-3 cursor-text rounded px-1 py-0.5 hover:bg-gray-50',
              dayGoal ? 'text-gray-600' : 'text-gray-400 italic'
            )}
            onClick={() => {
              setGoalDraft(dayGoal);
              setIsEditingGoal(true);
            }}
          >
            {dayGoal || 'Add description…'}
          </p>
        )}

        {/* Sessions — sortable. This SortableContext has NO DndContext of its
            own: it resolves to WeekCalendar's single shared context. That's
            what lets a session tile be dragged not just within this day
            (reorder) but onto another day card (cross-day move). The shared
            context uses `closestCorners` so variable-height tiles still
            resolve a drop target reliably (the same-day reorder fix). */}
        <SortableContext
          items={sessions.map((_, i) => `session:${date}:${i}`)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-4">
            {sessions.length === 0 ? (
              <div className="py-6 text-center text-gray-400 text-sm">
                No sessions on this day yet.
              </div>
            ) : (
              sessions.map((session, sessionIndex) => (
                <SortableSession
                  key={`session:${date}:${sessionIndex}`}
                  date={date}
                  session={session}
                  sessionIndex={sessionIndex}
                  dayGoal={dayGoal}
                  userActivities={userActivities}
                  onAddExercise={(idx) => setAddDialog({ open: true, sessionIndex: idx })}
                  onCategoryChange={(idx, cat) => updateSessionCategory(date, idx, cat)}
                  onNameChange={(idx, name) => updateSessionName(date, idx, name)}
                  onGoalChange={(idx, goal) => updateSessionGoal(date, idx, goal)}
                  onDelete={(idx) => deleteSession(date, idx)}
                />
              ))
            )}
          </div>
        </SortableContext>

        {/* Add a whole new session (Strength + Run, Lift + Yoga, etc.).
            Default modality is the user's first untagged activity so a
            single click reasonably tags a second cardio session. */}
        <button
          onClick={() => addSession(date, pickDefaultNewCategory(sessions))}
          className="w-full mt-3 px-3 py-2 text-sm border border-dashed border-gray-300 rounded-md hover:bg-gray-50 flex items-center justify-center gap-2 text-gray-700"
        >
          <Plus className="h-4 w-4" />
          Add Session
        </button>

        <AddExerciseDialog
          date={date}
          sessionIndex={addDialog.sessionIndex}
          isOpen={addDialog.open}
          onClose={() => setAddDialog({ open: false, sessionIndex: 0 })}
        />

        {/* Admin validation: thumbs up/down + notes feeding the
            ai_generation_feedback table for post-learning. */}
        <DayFeedback date={date} />
      </div>
    );
  }

  return null;
}

function MoveSubmenu({
  show,
  onToggle,
  targets,
  onPick,
}: {
  show: boolean;
  onToggle: () => void;
  targets: { date: string; label: string }[];
  onPick: (date: string) => void;
}) {
  return (
    <div className="border-t">
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between px-4 py-2 text-sm hover:bg-gray-50"
      >
        Move to date…
        <ArrowRight className={cn('size-3 transition-transform', show && 'rotate-90')} />
      </button>
      {show && (
        <div className="max-h-48 overflow-y-auto border-t">
          {targets.length === 0 && (
            <div className="px-4 py-2 text-xs text-muted-foreground">
              No other dates in the season.
            </div>
          )}
          {targets.map(({ date: d, label }) => (
            <button
              key={d}
              onClick={() => onPick(d)}
              className="block w-full text-left px-4 py-1.5 text-xs hover:bg-gray-50"
            >
              {label} <span className="text-muted-foreground">· {d}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
